import React, {useEffect, useMemo, useState} from "react";
import {MenuIngredient, MenuIngredientsGroup} from "../../../@types/stores/menu";
import {useTranslation} from "react-i18next";
import lodash from "lodash";
import {ReactComponent as IconCheck} from "../../../assets/icons/check.svg";
import {OrderLineIngredient} from "../../../@types/stores/order";
import {getAllergenById, getCurrency, getHost, getPreferenceById} from "../../../helpers/ApiHelper";
import {roundPrice} from "../../../helpers/orderHelper";
import menuStore from "../../../stores/menu";


export interface IngredientsGroupProps {
    ingredientsGroup: MenuIngredientsGroup;
    onChange: (id: string, selected: OrderLineIngredient[]) => void;
    selected: OrderLineIngredient[];
}

const IngredientsGroup = ({ingredientsGroup, onChange, selected}: IngredientsGroupProps) => {
    const [t] = useTranslation();

    const [ingredients, setIngredients] = useState<OrderLineIngredient[]>([]);

    useEffect(() => {
        setHeightToShortBlocks();
    }, []);

    useEffect(() => {
        if (!lodash.isEqual(selected, ingredients)) {
            setIngredients(selected);
        }
    }, [selected]);

    const setHeightToShortBlocks = () => {
        const allTagContainers = document.querySelectorAll(".tagContainer.short");
        const tagsByGroupId: {id: string; height: number}[] = [];
        allTagContainers.forEach(tag => {
            const id = tag.attributes.getNamedItem("x-data-groupId")?.value;
            if (typeof id !== "undefined") {
                const height = tag.clientHeight;

                const existedTag = tagsByGroupId.find(x => x.id === id);
                if (!existedTag) {
                    tagsByGroupId.push({id, height});
                }
                else if (existedTag.height < height) {
                    existedTag.height = height;
                }
            }
        });

        tagsByGroupId.forEach(tag => {
            document.querySelectorAll(`.tagContainer.short[x-data-groupId="${tag.id}"]`).forEach(dom => {
                // @ts-ignore
                dom.style.height = `${tag.height}px`;
            });
        });

    }

    const hasImage = useMemo(() => {
        for (const a of ingredientsGroup.items) {
            if (a.previewList)
                return true;
        }
        return false;
    }, [ingredientsGroup]);

    const changeIngredients = (ingredient: MenuIngredient) => {
        const maxCount = ingredientsGroup.max || Number.MAX_SAFE_INTEGER;
        const minCount = ingredientsGroup.min || Number.MIN_SAFE_INTEGER;
        if (maxCount === 1) {
            if (ingredients.some(x => x.id === ingredient.id)) {
                if (minCount < ingredients.length - 1) {
                    setIngredients(ingredients.filter(i => i.id !== ingredient.id));
                    onChange(ingredientsGroup.id, ingredients.filter(i => i.id !== ingredient.id));
                }
            } else {
                const _ingredient: OrderLineIngredient = {
                    id: ingredient.id,
                    number: ingredient.number,
                    quantity: ingredient.quantity,
                    price: ingredient.unitPrice,
                    sellUnit: ingredient.sellUnit,
                    default: ingredient.default || false
                };
                setIngredients([_ingredient]);
                onChange(ingredientsGroup.id, [_ingredient]);
            }
        } else {
            let newIngredients = lodash.cloneDeep(ingredients);
            if (newIngredients.some(x => x.id === ingredient.id)) {
                if (minCount < newIngredients.length)
                    newIngredients = newIngredients.filter(x => x.id !== ingredient.id);
            } else {
                if (newIngredients.length < maxCount) {
                    const _ingredient: OrderLineIngredient = {
                        id: ingredient.id,
                        number: ingredient.number,
                        quantity: ingredient.quantity,
                        price: ingredient.unitPrice,
                        sellUnit: ingredient.sellUnit,
                        default: ingredient.default || false
                    };
                    newIngredients.push(_ingredient);
                }
            }
            setIngredients(newIngredients);
            onChange(ingredientsGroup.id, newIngredients);
        }
    }

    return <div className="IngredientsGroup">
        <div className="text">
            {ingredientsGroup.name}
            <span className="min-max">
                {ingredientsGroup.min
                    ? <>{t("min")}: {ingredientsGroup.min}</>
                    : null
                }
                {ingredientsGroup.max
                    ? <span className="max">{t("max")}: {ingredientsGroup.max}</span>
                    : null
                }
            </span>
        </div>
        <div>
            <div className="blocks">
                {ingredientsGroup.items.map((i: MenuIngredient) =>
                    <div key={`ingredientsGroup_${ingredientsGroup.name}_addon_${i.id}`}
                         className={`block ${ingredients.some(x => x.id === i.id) ? "active" : ""}`}
                         onClick={() => changeIngredients(i)}>
                        {ingredients.some(x => x.id === i.id)
                            ? <IconCheck className="check"/>
                            : null
                        }
                        {hasImage
                            ? <>
                                <div className={`img ${i.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).length ? "blur" : ""}`}>
                                    {i.previewList
                                        ? <img alt={i.name} src={`${getHost()}${i.previewList}`}/>
                                        : null
                                    }
                                </div>
                                <div className="tagsImages">
                                    {i.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y)?.image ?
                                        <img key={`allergenTagImage_${y}`}
                                             src={`${getHost()}${getAllergenById(y)?.image || ""}`}
                                             alt={getAllergenById(y)?.name}/> : null)}
                                    {i.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y)?.image ?
                                        <img key={`preferenceTagImage_${y}`}
                                             src={`${getHost()}${getPreferenceById(y)?.image || ""}`}
                                             alt={getPreferenceById(y)?.name}/> : null)}
                                </div>
                            </>
                            : null
                        }
                        <div className="text">
                            <div x-data-groupId={`ingredientsGroup_${ingredientsGroup.id}`} className={`tagContainer ${!hasImage ? "short" : ""}`}>
                                <div className="tags">
                                    {i.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y) ?
                                        <div key={`allergenTag_${y}`}
                                             className="tag allergen">{getAllergenById(y)?.name || ""}</div> : null)}
                                    {i.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y) ?
                                        <div key={`preferenceTag_${y}`}
                                             className="tag preference">{getPreferenceById(y)?.name || ""}</div> : null)}
                                </div>
                            </div>
                            <div className="title">
                                {i.name}
                            </div>
                            {i.unitPrice
                                ? <div className="price">
                                    + {roundPrice(i.unitPrice * i.quantity).toFixed(2)} {getCurrency()}
                                </div>
                                : null
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
}

export default IngredientsGroup;
